import { InputMetaData, InputTypes } from '@cashier/cashier-model';

import { AMOUNT_FIELD_NAME, FIAT_AMOUNT } from '../../constants';
import {
  validateByCharactersType,
  validateByLimits,
  validateByScale,
} from './amountSpecificValidator';

interface ValidatorOptions {
  [InputTypes.Email](input: HTMLInputElement): boolean;
  [InputTypes.Number](input: HTMLInputElement): boolean;
  [InputTypes.CreditCardNumber](input: HTMLInputElement): boolean;
  [InputTypes.CreditCardVisa](input: HTMLInputElement): boolean;
  [InputTypes.CreditCardMastercard](input: HTMLInputElement): boolean;
  [InputTypes.Cvv](input: HTMLInputElement): boolean;
  [InputTypes.DateTime](input: HTMLInputElement): boolean;
  [InputTypes.Text](input: HTMLInputElement, params: InputMetaData): boolean;
  [InputTypes.PayzenPayTmAccountNumber](input: HTMLInputElement): boolean;
  [InputTypes.PayzenImpsAccountNumber](input: HTMLInputElement): boolean;
  [InputTypes.Integer](input: HTMLInputElement): boolean;
  [InputTypes.IdentificationNumber](input: HTMLInputElement): boolean;
  [InputTypes.Input](input: HTMLInputElement, params: InputMetaData): boolean;
}

export const CommonValidator: ValidatorOptions = {
  [InputTypes.Integer](number: HTMLInputElement): boolean {
    const { min, max, value } = number;

    if (!/^\d+$/.test(value)) return false;

    if (!min && !max) return true;

    if (min && !max) {
      return Number(value) >= Number(min);
    }

    if (max && !min) {
      return Number(value) <= Number(max);
    }

    return Number(value) >= Number(min) && Number(value) <= Number(max);
  },
  [InputTypes.IdentificationNumber](number: HTMLInputElement): boolean {
    const { value } = number;

    return /^[0-9-]{8,11}$/.test(value);
  },
  [InputTypes.PayzenPayTmAccountNumber](number: HTMLInputElement): boolean {
    const { value } = number;

    return /^\d+$/.test(value) && value.length === 10;
  },
  [InputTypes.PayzenImpsAccountNumber](number: HTMLInputElement): boolean {
    const { value } = number;

    return /^\d+$/.test(value) && value.length >= 10 && value.length <= 16;
  },
  [InputTypes.Email](email: HTMLInputElement): boolean {
    // eslint-disable-next-line max-len
    return /^((([a-z]|\d|[!#\$%&'\*\+\-\/=\?\^_`{\|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+(\.([a-z]|\d|[!#\$%&'\*\+\-\/=\?\^_`{\|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+)*)|((\x22)((((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(([\x01-\x08\x0b\x0c\x0e-\x1f\x7f]|\x21|[\x23-\x5b]|[\x5d-\x7e]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(\\([\x01-\x09\x0b\x0c\x0d-\x7f]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]))))*(((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(\x22)))@((([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.)+(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))$/i.test(
      email.value,
    );
  },
  [InputTypes.Number](number: HTMLInputElement): boolean {
    const { min, max, value } = number;

    if (!min && !max) return true;

    if (min && !max) {
      return Number(value) >= Number(min);
    }

    if (max && !min) {
      return Number(value) <= Number(max);
    }

    return Number(value) >= Number(min) && Number(value) <= Number(max);
  },
  [InputTypes.CreditCardNumber](number: HTMLInputElement): boolean {
    const { value } = number;
    return value.length <= 23 && value.length >= 19;
  },
  [InputTypes.CreditCardVisa](number: HTMLInputElement): boolean {
    const { value } = number;
    return /^4\d{3}([ -]?)\d{4}\1\d{4}\1\d{4}$/.test(value);
  },
  [InputTypes.CreditCardMastercard](number: HTMLInputElement): boolean {
    const { value } = number;
    return /^5[1-5]\d{2}([ -]?)\d{4}\1\d{4}\1\d{4}$/.test(value);
  },
  [InputTypes.Cvv](number: HTMLInputElement): boolean {
    const { value } = number;
    return /^[0-9]{3,4}$/.test(value);
  },
  [InputTypes.DateTime](date: HTMLInputElement): boolean {
    const { value } = date;

    if (!value.includes('_')) {
      const month = value.substring(0, 2);
      const yearPart = value.substring(3, 7);
      const fullYear = yearPart.length === 2 ? `20${yearPart}` : yearPart;
      const dateString = `${fullYear}-${month}-01`;

      const expiryDate = new Date(dateString);
      const currentDate = new Date();
      currentDate.setHours(0, 0, 0, 0);

      return expiryDate >= currentDate;
    }

    return false;
  },
  [InputTypes.Input](input: HTMLInputElement, params: InputMetaData): boolean {
    const { value, name, min, max } = input || {};

    if (name === FIAT_AMOUNT) {
      if (!validateByCharactersType(value)) {
        return false;
      }

      const isValidByLimits = validateByLimits(value, Number(min), Number(max));
      const isValidByScale = validateByScale(value, params);

      return isValidByLimits && isValidByScale;
    }

    return value?.trim() !== '';
  },
  [InputTypes.Text](input: HTMLInputElement, params: InputMetaData): boolean {
    const { value, name, min, max } = input || {};

    if (name === AMOUNT_FIELD_NAME) {
      const { balance } = params || { balance: 0 };
      if (balance !== null && balance < Number(value.replace(',', '.'))) {
        return false;
      }

      if (!validateByCharactersType(value)) {
        return false;
      }

      const isValidByLimits = validateByLimits(value, Number(min), Number(max));
      const isValidByScale = validateByScale(value, params);

      return isValidByLimits && isValidByScale;
    }

    return true;
  },
};
